import api from "../../../utils/api";
import { listBases } from "../../bases-propiedades/services/basesApi";
import type {
  Asset,
  AssetAttachment,
  AssetErrorCode,
  AssetEvent,
  AssetInput,
  AssetMaintenance,
  AssetPurchase,
  AssetsSummary,
  AssetStatus,
  AssetSupplier,
  AssignInput,
  AttachmentKind,
  BookValue,
  FinanceLink,
  MaintenanceCloseInput,
  MaintenanceOpenInput,
  PurchaseInput,
  RetireInput,
  SupplierInput,
  SupplierRole,
  ValidationIssue,
  WarrantyStatus,
} from "@torre/assets";

// ============================================================================
// Cliente del API de Activos (`modules/assets`, PR 2 de la épica #1638).
// ============================================================================
// Archivo común, idéntico en CC-CONSOLE y WAYAK. Rutas planas como Gastos
// (`/assets` lo ocupa `express.static`). Las respuestas vienen como
// `{ meta, data }`: se devuelve `data`.
//
// La compañía la decide el servidor (perfil o token): `x-company-id` se
// ignora. Los montos llegan en `null` sin `feat:activos.costos` (D6), así que
// la pantalla nunca decide sola quién ve costos: se lo dice el API.
//
// Un error de negocio responde `meta.code` estable y el detalle en `data`
// (`{ assetId }`, `{ deviceId }`, `{ purchaseId }`, `{ supplierId }` o
// `{ errors: [{ field, code }] }`): `assetsErrorOf` lo deja a la mano.
// ============================================================================

export type AssetRow = Asset & { id: string; retiredNote?: string | null };

export interface AssetDetail extends AssetRow {
  computed: {
    /** Valor en libros a hoy (línea recta, informativo); null sin costos. */
    bookValue: BookValue | null;
    warranty: { status: WarrantyStatus; daysLeft: number | null };
    asOf: string;
  };
}

export type AssetEventRow = AssetEvent & { id: string };

/** Compra tal como la sirve el API: sin costos, `finance` llega en null. */
export type PurchaseRow = Omit<AssetPurchase, "finance"> & {
  id: string;
  assetIds?: string[];
  finance: FinanceLink | null;
};

export interface PurchaseDetail extends PurchaseRow {
  assets: AssetRow[];
}

export type MaintenanceRow = Omit<AssetMaintenance, "finance"> & {
  id: string;
  finance: AssetMaintenance["finance"] | null;
};

export type SupplierRow = AssetSupplier & { id: string };

export interface SummaryResponse extends AssetsSummary {
  /** El conteo se cortó por tope (inventarios muy grandes). */
  truncated: boolean;
  /** D1 (d): equipos de rastreo de sólo lectura; `investedBase` sólo para el owner. */
  tracking: { devices: number; investedBase: number | null };
}

export interface Page<T> {
  items: T[];
  nextCursor: string | null;
}

export interface AssetFilters {
  status?: AssetStatus | "";
  category?: string;
  custodianId?: string;
  vehicleId?: string;
  supplierId?: string;
  q?: string;
}

/** Persona que puede resguardar un activo (`/userList/light`). */
export interface Person {
  id: string;
  name: string;
  email: string | null;
}

export interface Vehicle {
  id: string;
  name: string;
}

export interface BaseOption {
  id: string;
  name: string;
}

/** Códigos que el API usa además de los del paquete. */
export type ActivosErrorCode = AssetErrorCode | "ENTITLEMENT_REQUIRED" | "COMPANY_SCOPE_FORBIDDEN" | "NO_ENCONTRADO";

export interface ActivosError {
  status: number | null;
  code: ActivosErrorCode | string | null;
  message: string | null;
  data: {
    assetId?: string;
    deviceId?: string;
    purchaseId?: string;
    supplierId?: string;
    field?: string;
    errors?: ValidationIssue[];
  } | null;
}

/** Status, `meta.code`, mensaje y detalle de un error del API. */
export function assetsErrorOf(err: unknown): ActivosError {
  const r = (err as { response?: { status?: number; data?: { meta?: { code?: string; message?: string }; data?: unknown } } })?.response;
  const data = r?.data?.data;
  return {
    status: r?.status ?? null,
    code: r?.data?.meta?.code ?? null,
    message: r?.data?.meta?.message ?? null,
    data: data && typeof data === "object" && !Array.isArray(data) ? (data as ActivosError["data"]) : null,
  };
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const data = <T>(res: any): T => res?.data?.data as T;

/** Quita los filtros vacíos (el API valida cada uno). */
const clean = (params: Record<string, unknown>) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""));

const fullName = (u: { first_name?: string; last_name?: string; email?: string }) =>
  [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email || "";

export const activosApi = {
  // --- Inventario -------------------------------------------------------------------
  summary: async () => data<SummaryResponse>(await api.get("/assetsSummary")),
  list: async (filters: AssetFilters = {}, cursor?: string | null, limit = 50) =>
    data<Page<AssetRow>>(await api.get("/assetsList", { params: clean({ ...filters, cursor, limit }) })),
  get: async (id: string) => data<AssetDetail>(await api.get("/assetItem", { params: { id } })),
  create: async (input: AssetInput) => data<AssetRow>(await api.post("/assetItem", input)),
  update: async (id: string, input: AssetInput) => data<AssetRow>(await api.put("/assetItem", input, { params: { id } })),
  events: async (id: string, cursor?: string | null) =>
    data<Page<AssetEventRow>>(await api.get("/assetEvents", { params: clean({ id, cursor, limit: 50 }) })),
  assign: async (id: string, input: AssignInput) => data<AssetRow>(await api.post("/assetAssign", input, { params: { id } })),
  returnToStock: async (id: string) => data<AssetRow>(await api.post("/assetAssign", { returnToStock: true }, { params: { id } })),
  retire: async (id: string, input: RetireInput) => data<AssetRow>(await api.post("/assetRetire", input, { params: { id } })),

  // --- Mantenimiento simple ---------------------------------------------------------
  maintenance: async (assetId: string) =>
    data<Page<MaintenanceRow>>(await api.get("/assetMaintenance", { params: { assetId, limit: 50 } })),
  openMaintenance: async (assetId: string, input: MaintenanceOpenInput) =>
    data<MaintenanceRow>(await api.post("/assetMaintenance", { ...input, assetId })),
  closeMaintenance: async (id: string, input: MaintenanceCloseInput) =>
    data<MaintenanceRow>(await api.put("/assetMaintenance", input, { params: { id } })),

  // --- Compras ------------------------------------------------------------------------
  purchases: async (supplierId?: string, cursor?: string | null) =>
    data<Page<PurchaseRow>>(await api.get("/assetPurchaseList", { params: clean({ supplierId, cursor, limit: 50 }) })),
  purchase: async (id: string) => data<PurchaseDetail>(await api.get("/assetPurchase", { params: { id } })),
  createPurchase: async (input: PurchaseInput) => data<PurchaseDetail>(await api.post("/assetPurchase", input)),
  updatePurchase: async (id: string, input: PurchaseInput) =>
    data<PurchaseRow>(await api.put("/assetPurchase", input, { params: { id } })),

  // --- Adjuntos (Storage sólo con URL firmada por el API) -----------------------------
  addAttachment: async (target: { purchaseId?: string; assetId?: string }, file: File, kind?: AttachmentKind) => {
    const form = new FormData();
    form.append("file", file);
    if (kind) form.append("kind", kind);
    return data<AssetAttachment>(
      await api.post("/assetAttachment", form, { params: target, headers: { "Content-Type": "multipart/form-data" } }),
    );
  },
  removeAttachment: async (target: { purchaseId?: string; assetId?: string }, path: string) =>
    data<{ path: string }>(await api.delete("/assetAttachment", { params: { ...target, path } })),
  attachmentUrl: async (path: string) =>
    data<{ url: string; expiresInSeconds: number }>(await api.get("/assetAttachmentUrl", { params: { path } })),

  // --- Proveedores ----------------------------------------------------------------------
  suppliers: async (params: { role?: SupplierRole | ""; q?: string } = {}) =>
    data<{ items: SupplierRow[]; truncated: boolean }>(await api.get("/assetSupplierList", { params: clean(params) })),
  createSupplier: async (input: SupplierInput) => data<SupplierRow>(await api.post("/assetSupplier", input)),
  updateSupplier: async (id: string, input: SupplierInput) =>
    data<SupplierRow>(await api.put("/assetSupplier", input, { params: { id } })),

  // --- Catálogos de la compañía para asignar (sólo se referencian) ---------------------
  people: async (): Promise<Person[]> => {
    const list = data<Array<{ id: string; first_name?: string; last_name?: string; email?: string }>>(await api.get("/userList/light"));
    return (Array.isArray(list) ? list : []).map((u) => ({ id: u.id, name: fullName(u), email: u.email ?? null }));
  },
  vehicles: async (): Promise<Vehicle[]> => {
    const list = data<Array<{ id: string; name?: string; plate?: string }>>(await api.get("/vehicleList", { params: { page: 1, limit: 1000 } }));
    return (Array.isArray(list) ? list : []).map((v) => ({
      id: v.id,
      name: [v.name, v.plate].filter(Boolean).join(" · ") || v.id,
    }));
  },
  bases: async (): Promise<BaseOption[]> => (await listBases()).map((b) => ({ id: b.id, name: b.name || b.id })),
};
